import React, { useEffect, useRef } from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { Color } from '../../../styles/Color';
import { IDropdownItem } from '../DropdownList/DropdownItem';
import { IGetItemDisplayValue, IGetItemKey } from '../DropdownList/helpers';
import { Checkbox } from '../Checkbox';
import { ITheme, themed } from '../../themer';
import { Css, Responsive } from '../../utils';
import { TextButton } from '../Button';
import { UnstyledLi } from '../List';
import { calculateDropdownMaxHeight } from '../../utils/calculateDropdownMaxHeight';

interface IStyledMultiSelectDropdownListWrapProps {
	isOpen?: boolean;
}

interface IMultiSelectDropdownListProps {
	getMenuProps: PropGetters<IDropdownItem>['getMenuProps'];
	getItemProps: PropGetters<IDropdownItem>['getItemProps'];
	getItemDisplayValue: IGetItemDisplayValue;
	getItemKey: IGetItemKey;
	items: IDropdownItem[];
	isOpen: boolean;
	selectedItems: IDropdownItem[];
	setSelectedItems: (selectedItems: IDropdownItem[]) => void;
	onChange: (selectedItems: IDropdownItem[]) => void;
}

export default function MultiSelectDropdownList({
	getItemDisplayValue,
	getMenuProps,
	getItemProps,
	getItemKey,
	isOpen,
	items,
	selectedItems,
	setSelectedItems,
	onChange,
}: IMultiSelectDropdownListProps) {
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		onChange(selectedItems);
	}, [selectedItems]);

	const handleSelectItem = (item: IDropdownItem) => {
		// We check if the item is already selected. If it is, we filter it out (deselect it), otherwise we add it to the array (select it)
		const updatedSelection = selectedItems.includes(item)
			? selectedItems.filter((selectedItem) => selectedItem !== item)
			: [...selectedItems, item];

		setSelectedItems(updatedSelection);
	};

	const onSelectAll = () => {
		setSelectedItems(items);
	};

	const onClearAll = () => {
		setSelectedItems([]);
	};

	const renderDropdown = () => (
		<StyledMultiSelectDropdownList
			{...getMenuProps({ ref: listRef }, { suppressRefError: true })}
			isOpen={isOpen}
			role="listbox"
			aria-multiselectable="true"
			itemCount={items.length}
		>
			<ListItemsWrap>
				<ListItemsTitle>Filters</ListItemsTitle>

				{items.map((item: IDropdownItem, index: number) => (
					<UnstyledLi
						key={getItemKey(item)}
						tabIndex={0}
						className="no-max-width"
						onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								handleSelectItem(item);
							}
						}}
					>
						<StyledCheckbox
							label={getItemDisplayValue(item)}
							checked={selectedItems?.includes(item)}
							onChange={() => handleSelectItem(item)}
							tabIndex={-1}
							aria-checked={selectedItems?.includes(item)}
							{...getItemProps({
								index,
								item,
							})}
						/>
					</UnstyledLi>
				))}
			</ListItemsWrap>

			<FilterButtonWrap>
				<FilterButton onClick={onSelectAll}>Select all</FilterButton>
				<FilterButton onClick={onClearAll}>Clear all</FilterButton>
			</FilterButtonWrap>
		</StyledMultiSelectDropdownList>
	);

	return (
		<StyledMultiSelectDropdownListWrap isOpen={isOpen}>
			{isOpen && renderDropdown()}
		</StyledMultiSelectDropdownListWrap>
	);
}

const StyledMultiSelectDropdownListWrap = styled.div<
	IStyledMultiSelectDropdownListWrapProps,
	ITheme
>`
	position: relative;
	display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

export const StyledMultiSelectDropdownList = styled.div<
	{ maxDisplayedItems?: number; itemCount: number },
	ITheme
>`
	position: absolute;
	min-width: 320px;
	width: 100%;
	overflow-y: auto;
	border: 1px solid ${Color.BorderCard};
	border-radius: 8px;
	max-height: ${({ itemCount }) => calculateDropdownMaxHeight({ itemCount })};
	z-index: ${themed(({ dropdownList }) => dropdownList.options.zIndex)};
	background: ${Color.Neutral2};
	display: flex;
	padding: ${Css.pxToRem(24)} ${Css.pxToRem(16)} ${Css.pxToRem(0)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		min-width: unset;
		max-height: ${({ itemCount }) =>
			calculateDropdownMaxHeight({
				itemCount,
				itemHeight: Css.pxToRem(72),
				maxDisplayItemsMobile: 4,
			})};
	}
`;

const ListItemsWrap = styled.div``;

const ListItemsTitle = styled.div`
	font-weight: 500;
	font-size: ${Css.pxToRem(24)};
`;

const FilterButtonWrap = styled.div`
	display: flex;
	align-items: baseline;
	gap: ${Css.pxToRem(24)};
	margin-top: ${Css.pxToRem(1)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(5)};
	}
`;

const FilterButton = styled(TextButton)<{}, ITheme>`
	font-size: ${Css.pxToRem(16)};
	font-weight: 700;

	&:hover {
		color: ${({ theme }) => theme.button.textButton.hover.font.color};
	}
`;

const StyledCheckbox = styled(Checkbox)`
	padding: ${Css.pxToRem(20)} 0;
	cursor: pointer;
`;
